import axios from "axios";

export function parseHeaders(headersString) {
  if (!headersString?.trim()) return {};
  try {
    return JSON.parse(headersString);
  } catch {
    return {};
  }
}

export function substituteFlowVariables(text) {
  if (!text || typeof text !== "string") return text;
  if (!window.flowVariables || !text.includes("${")) return text;
  return text.replace(/\$\{([^}]+)\}/g, (match, varName) => {
    const val = window.flowVariables[varName];
    return val !== undefined ? val : match;
  });
}

export function buildUrlWithParams(url, queryParams = []) {
  const baseUrl = url || "";
  const valid = (queryParams || []).filter((p) => p?.key);
  if (!valid.length) return baseUrl;
  const qs = valid
    .map(
      (p) =>
        `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value || "")}`
    )
    .join("&");
  return baseUrl.includes("?") ? `${baseUrl}&${qs}` : `${baseUrl}?${qs}`;
}

export function extractValueByPath(obj, path) {
  if (!path) return undefined;
  const parts = path.split(".");
  let value = obj;
  for (const part of parts) {
    if (value && typeof value === "object" && part in value) {
      value = value[part];
    } else {
      return undefined;
    }
  }
  return value;
}

export function getJsonPaths(obj, prefix = "") {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  let paths = [];
  for (const key of Object.keys(obj)) {
    const currentPath = prefix ? `${prefix}.${key}` : key;
    paths.push(currentPath);
    if (obj[key] !== null && typeof obj[key] === "object" && !Array.isArray(obj[key])) {
      paths = paths.concat(getJsonPaths(obj[key], currentPath));
    }
  }
  return paths;
}

/**
 * Executa requisição HTTP de teste (mesma lógica do nó V1 / Editor legado).
 */
export async function runHttpRequestTest({
  url,
  method = "GET",
  headersString = "",
  requestBody = "",
  queryParams = [],
  timeout = 10000,
  saveVariables = [],
}) {
  const customHeaders = parseHeaders(headersString);
  const processedUrl = substituteFlowVariables(url);
  const finalUrl = buildUrlWithParams(processedUrl, queryParams);
  const upperMethod = (method || "GET").toUpperCase();
  const hasBody = !["GET", "DELETE"].includes(upperMethod);

  const config = {
    method: upperMethod,
    url: finalUrl,
    headers: { "Content-Type": "application/json", ...customHeaders },
    timeout: Number(timeout) || 10000,
  };

  if (hasBody) {
    let processedBody = requestBody || "{}";
    if (window.flowVariables && processedBody.includes("${")) {
      processedBody = processedBody.replace(/\$\{([^}]+)\}/g, (match, varName) => {
        const val = window.flowVariables[varName];
        return val !== undefined ? JSON.stringify(val) : match;
      });
    } else {
      processedBody = substituteFlowVariables(processedBody);
    }
    try {
      config.data = JSON.parse(processedBody || "{}");
    } catch {
      config.data = processedBody;
    }
  }

  const res = await axios(config);
  const responseData = res.data;

  if (window.setFlowVariable) {
    window.setFlowVariable("apiResponse", responseData);
  } else {
    if (!window.flowVariables) window.flowVariables = {};
    window.flowVariables.apiResponse = responseData;
  }

  const extracted = {};
  const responseVariables = [];

  for (const item of saveVariables || []) {
    if (!item?.path || !item?.variable) continue;
    const value = extractValueByPath(responseData, item.path);
    if (value !== undefined && value !== null) {
      extracted[item.variable] = value;
      responseVariables.push({
        path: item.path,
        variableName: item.variable,
      });
      if (window.setFlowVariable) {
        window.setFlowVariable(item.variable, value);
      } else {
        window.flowVariables[item.variable] = value;
      }
      window.dispatchEvent(
        new CustomEvent("flowVariableUpdate", {
          detail: { name: item.variable, value },
        })
      );
    }
  }

  return {
    ok: true,
    data: responseData,
    jsonPaths: getJsonPaths(
      responseData && typeof responseData === "object" ? responseData : {}
    ),
    responseVariables,
    extracted,
  };
}

export function formatHttpTestError(error) {
  if (error?.response?.data !== undefined) return error.response.data;
  return error?.message || "Erro na requisição";
}
